/**
 * Renders an artifact's body content with the right viewer for the
 * mime type, plus a header row (Content label on left, mode toggle +
 * external-link + download + optional Copy on the right).
 *
 * Mode toggle pref ("text vs. native") is persisted in localStorage so
 * the user's last choice is remembered across artifacts and kinds.
 *
 * Modes per content type:
 *   email with body_html         → Text (default) | HTML
 *   .docx with cached_file       → Text (default) | Rendered (mammoth.js)
 *   .pdf with cached_file        → Text (default) | PDF (embed)
 *   .html file (text/html)       → Text (default) | HTML (sanitized)
 *   image                        → inline always (no toggle)
 *   plain text                   → text only
 *   Google native (no content)   → external "Open in Google …" link
 *   other binary fallback        → download link only
 */

import { useEffect, useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import { Download, Copy, Check, ExternalLink } from 'lucide-react'

type Props = {
  mime: string
  cachedContent: string
  bodyHtml?: string
  cachedFileUrl: string | null
  cachedFileSize?: number | null
  storageMode: string
  /** Source system identifier — used to construct external "open in Google
   *  Drive / Docs / Sheets / Slides" links for gdrive-sourced artifacts
   *  whose native format isn't downloadable as a file. */
  sourceType?: string
  sourceExternalId?: string
  /** Height for embed/iframe. Number → pixels. String → any valid CSS length. */
  pdfHeight?: number | string
  /** Header label shown to the left of the toggle. Defaults to "Content". */
  headerLabel?: string
  /** Optional copy callback. When provided, a "Copy" button appears
   *  rightmost in the header row. */
  onCopy?: () => void
  /** Briefly true after a successful copy; flips the button to "Copied". */
  copied?: boolean
}

type Mode = 'text' | 'native'
type Kind = 'image' | 'pdf' | 'docx' | 'html' | 'htmlfile' | 'text' | 'binary'

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const PREF_KEY = 'me.bodypreview.preferText'

/** Map a Google-native mime + Drive file_id to a "open in Google …" URL.
 *  Returns null for non-Google or non-renderable mimes. */
function googleAppUrl(mime: string, externalId: string | undefined | null): { url: string; label: string } | null {
  if (!externalId) return null
  switch ((mime || '').toLowerCase()) {
    case 'application/vnd.google-apps.document':
      return { url: `https://docs.google.com/document/d/${externalId}/edit`, label: 'Open in Docs' }
    case 'application/vnd.google-apps.spreadsheet':
      return { url: `https://docs.google.com/spreadsheets/d/${externalId}/edit`, label: 'Open in Sheets' }
    case 'application/vnd.google-apps.presentation':
      return { url: `https://docs.google.com/presentation/d/${externalId}/edit`, label: 'Open in Slides' }
    case 'application/vnd.google-apps.form':
      return { url: `https://docs.google.com/forms/d/${externalId}/edit`, label: 'Open in Forms' }
    case 'application/vnd.google-apps.folder':
      return { url: `https://drive.google.com/drive/folders/${externalId}`, label: 'Open in Drive' }
    default:
      return null
  }
}

function getPreferText(): boolean {
  try {
    const v = localStorage.getItem(PREF_KEY)
    return v === null ? true : v === '1'
  } catch { return true }
}
function setPreferText(v: boolean) {
  try { localStorage.setItem(PREF_KEY, v ? '1' : '0') } catch { /* ignore */ }
}

function prettySize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function detectKind(mime: string, hasFileUrl: boolean, hasBodyHtml: boolean, hasText: boolean): Kind {
  const m = (mime || '').toLowerCase()
  if (hasFileUrl && m.startsWith('image/')) return 'image'
  if (hasFileUrl && m === 'application/pdf') return 'pdf'
  if (hasFileUrl && m === DOCX_MIME) return 'docx'
  // text/html artifacts (e.g. SecureMessageAtt.html attachments, exported
  // appeal docs): cached_content IS the html source, so we render that
  // sanitized for the native view and the same string for the text view.
  if (m === 'text/html' && hasText) return 'htmlfile'
  if (hasBodyHtml) return 'html'
  if (hasText) return 'text'
  if (hasFileUrl) return 'binary'
  return 'text'
}

function nativeLabelFor(kind: Kind): string {
  switch (kind) {
    case 'pdf': return 'PDF'
    case 'docx': return 'Rendered'
    case 'html': return 'HTML'
    case 'htmlfile': return 'HTML'
    default: return 'Native'
  }
}

export default function BodyPreview({
  mime, cachedContent, bodyHtml, cachedFileUrl, cachedFileSize, storageMode,
  sourceType, sourceExternalId,
  pdfHeight = 700, headerLabel = 'Content', onCopy, copied = false,
}: Props) {
  const hasText = !!(cachedContent && cachedContent.length)
  const hasBodyHtml = !!(bodyHtml && bodyHtml.trim())
  const hasFileUrl = !!cachedFileUrl
  const kind = detectKind(mime, hasFileUrl, hasBodyHtml, hasText)
  const supportsToggle =
    (kind === 'pdf' && hasText) ||
    (kind === 'docx' && hasText) ||
    (kind === 'html' && hasText) ||
    (kind === 'htmlfile' && hasText)
  // For gdrive-sourced Google-native artifacts (Sheets, Docs, Slides,
  // Forms, Folders), construct a deep link into the Google web UI. We
  // don't host the file; the link is the only way to view content for
  // mimes whose text wasn't extracted (Sheets) and a useful "go to
  // source" alternative for the rest.
  const externalLink = sourceType === 'gdrive' ? googleAppUrl(mime, sourceExternalId) : null

  // Pick a starting mode for this artifact: image/binary sit on 'native',
  // plain text on 'text', and toggleable kinds (pdf/docx/html with text)
  // honor the localStorage pref.
  const computeInitial = (): Mode => {
    if (kind === 'image' || kind === 'binary') return 'native'
    if (kind === 'text') return 'text'
    if (!hasText) return 'native'
    return getPreferText() ? 'text' : 'native'
  }
  const [mode, setMode] = useState<Mode>(computeInitial)

  // When the parent swaps in an artifact of a different kind (or the
  // text-availability flips), recompute mode from the pref so the new
  // artifact respects the user's standing preference.
  useEffect(() => {
    setMode(computeInitial())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, hasText])

  const onModeChange = (m: Mode) => {
    setMode(m)
    setPreferText(m === 'text')
  }

  return (
    <div>
      <div className="me-section-h flex items-center justify-between gap-2">
        <span>{headerLabel}</span>
        <div className="flex items-center gap-1.5">
          {supportsToggle && (
            <ToggleBar
              modes={[
                { id: 'text', label: 'Text' },
                { id: 'native', label: nativeLabelFor(kind) },
              ]}
              value={mode}
              onChange={onModeChange}
            />
          )}
          {externalLink && (
            <a
              href={externalLink.url}
              target="_blank"
              rel="noreferrer"
              className="me-btn me-btn-outline"
              title="Open the original Google file in a new tab"
            >
              <ExternalLink size={11} /> {externalLink.label}
            </a>
          )}
          {hasFileUrl && (
            <a
              href={cachedFileUrl!}
              download
              className="me-btn me-btn-outline"
              title={`download${cachedFileSize ? ` (${prettySize(cachedFileSize)})` : ''}`}
            >
              <Download size={11} /> download
            </a>
          )}
          {onCopy && (
            <button
              onClick={onCopy}
              className="me-btn me-btn-outline"
              title="Copy a formatted version (header + body + comments + redlines) to clipboard"
            >
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </div>

      {kind === 'image' ? (
        <div className="rounded-md bg-[color:var(--me-panel)] p-3 text-center">
          <img src={cachedFileUrl!} alt="artifact" className="mx-auto max-h-[600px] rounded-sm" />
        </div>
      ) : kind === 'binary' ? (
        <div className="rounded-md bg-[color:var(--me-panel)] p-4 text-[12px] me-text-mute italic">
          Binary content of type <code className="me-mono">{mime || 'unknown'}</code> — no inline preview.{' '}
          <a href={cachedFileUrl!} target="_blank" rel="noreferrer" className="underline">Download</a>.
        </div>
      ) : mode === 'text' ? (
        hasText ? (
          <TextBlock text={cachedContent} />
        ) : (
          <NoContentBlock mime={mime} storageMode={storageMode} externalLink={externalLink} />
        )
      ) : kind === 'html' ? (
        <HtmlBody html={bodyHtml || ''} />
      ) : kind === 'htmlfile' ? (
        <HtmlBody html={cachedContent} />
      ) : kind === 'pdf' ? (
        <PdfBody url={cachedFileUrl!} pdfHeight={pdfHeight} />
      ) : kind === 'docx' ? (
        <DocxBody url={cachedFileUrl!} />
      ) : hasText ? (
        <TextBlock text={cachedContent} />
      ) : (
        <NoContentBlock mime={mime} storageMode={storageMode} externalLink={externalLink} />
      )}
    </div>
  )
}

function ToggleBar({ modes, value, onChange }: {
  modes: { id: Mode; label: string }[]
  value: Mode
  onChange: (v: Mode) => void
}) {
  return (
    <div className="flex gap-1 text-[10.5px]">
      {modes.map((m) => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          className={`px-2 py-0.5 rounded-sm me-mono uppercase tracking-wider ${
            value === m.id
              ? 'bg-[color:var(--me-accent-soft)] me-text'
              : 'me-text-mute hover:bg-[color:var(--me-panel)]'
          }`}
        >
          {m.label}
        </button>
      ))}
    </div>
  )
}

/** Friendly fallback when there's nothing inline to show. Surfaces the
 *  external Google link (if any) prominently — otherwise a small
 *  "no content" notice. */
function NoContentBlock({ mime, storageMode, externalLink }: {
  mime: string
  storageMode: string
  externalLink: { url: string; label: string } | null
}) {
  if (externalLink) {
    return (
      <div className="rounded-md bg-[color:var(--me-panel)] p-4 text-[13px] me-text-dim">
        Inline content not extracted for <code className="me-mono">{mime || 'unknown'}</code>.
        <div className="mt-2">
          <a
            href={externalLink.url}
            target="_blank"
            rel="noreferrer"
            className="me-btn me-btn-primary inline-flex items-center gap-1"
          >
            <ExternalLink size={12} /> {externalLink.label}
          </a>
        </div>
      </div>
    )
  }
  return (
    <div className="text-[12px] me-text-mute italic">
      No cached content. (Storage mode: {storageMode}.)
    </div>
  )
}

function TextBlock({ text }: { text: string }) {
  return (
    <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-4 text-[12.5px] leading-relaxed me-text">
      {text}
    </pre>
  )
}

function HtmlBody({ html }: { html: string }) {
  // Sanitize: drop scripts, event handlers, embeds. Keep links + images +
  // styles + tables (legitimate email/doc content).
  const sanitized = useMemo(() => DOMPurify.sanitize(html, {
    FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  }), [html])
  return (
    <div
      className="rounded-md bg-white p-4 text-[13px] leading-relaxed text-black overflow-x-auto"
      // Sanitized via DOMPurify above; no script/event-handler attrs survive.
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  )
}

function PdfBody({ url, pdfHeight }: { url: string; pdfHeight: number | string }) {
  return (
    <embed
      src={url}
      type="application/pdf"
      className="block w-full rounded-md border me-border"
      style={{ height: pdfHeight }}
    />
  )
}

function DocxBody({ url }: { url: string }) {
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (html !== null) return
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const r = await fetch(url, { credentials: 'include' })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const buf = await r.arrayBuffer()
        const mammoth = await import('mammoth')
        const { value } = await mammoth.convertToHtml({ arrayBuffer: buf })
        if (cancelled) return
        const sanitized = DOMPurify.sanitize(value, {
          FORBID_TAGS: ['script', 'object', 'embed', 'iframe', 'form', 'input', 'button'],
          FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
        })
        setHtml(sanitized)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'render failed')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [url, html])

  if (loading) return <div className="text-[12px] me-text-mute italic">Rendering .docx…</div>
  if (error) return (
    <div className="text-[12px] me-text-mute italic">
      Couldn't render: {error}.{' '}
      <a href={url} target="_blank" rel="noreferrer" className="underline">Download</a>.
    </div>
  )
  if (!html) return <div className="text-[12px] me-text-mute italic">Loading…</div>
  return (
    <div
      className="rounded-md bg-white p-4 text-[13px] leading-relaxed text-black overflow-x-auto"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}
